var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ADD_WAREHOUSE_RELEASE from 'api/mutations/ADD_WAREHOUSE_RELEASE';
import DUPLICATE_WAREHOUSE_RELEASE from 'api/mutations/DUPLICATE_WAREHOUSE_RELEASE';
import REMOVE_WAREHOUSE_RELEASE from 'api/mutations/REMOVE_WAREHOUSE_RELEASE';
import GET_WAREHOUSE_RELEASES from 'api/queries/GET_WAREHOUSE_RELEASES';
import { WAREHOUSE_RELEASES_PAGE_SIZE } from 'constants/pagination';
import client from '../api/environment';
const initialState = {
    items: [],
    filters: {
        search: '',
        order: '-modifiedDate',
        statuses: [],
    },
    pageInfo: {
        hasNextPage: false,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        remove: 'idle',
        duplicate: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        remove: null,
        duplicate: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        remove: false,
        duplicate: false,
    },
    cursors: [''],
    currentPage: 1,
    totalCount: null,
};
export const fetchWarehouseReleases = createAsyncThunk('warehouseReleases/fetchWarehouseReleases', (arg, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const afterCursor = (arg === null || arg === void 0 ? void 0 : arg.afterCursor) || '';
    const state = getState();
    const { filters } = state.warehouseReleases;
    try {
        const response = yield client.query({
            query: GET_WAREHOUSE_RELEASES,
            variables: {
                orderBy: filters.order || null,
                statuses: filters.statuses.length > 0 ? filters.statuses : null,
                search: filters.search || null,
                first: WAREHOUSE_RELEASES_PAGE_SIZE,
                after: afterCursor,
            },
            fetchPolicy: 'network-only',
        });
        return {
            warehouseReleases: response.data.me.warehouseReleases.edges.map((node) => node.node),
            pageInfo: response.data.me.warehouseReleases.pageInfo,
            afterCursor,
            totalCount: response.data.me.warehouseReleasesCount,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const goToWarehouseReleasesPage = createAsyncThunk('warehouseReleases/goToPage', (pageNumber, { dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    if (pageNumber < 1) {
        return rejectWithValue('Invalid page number');
    }
    const cursor = pageNumber === 1 ? '' : `offset:${(pageNumber - 1) * WAREHOUSE_RELEASES_PAGE_SIZE}`;
    return dispatch(fetchWarehouseReleases({ afterCursor: cursor }));
}));
export const goToNextWarehouseReleasesPage = createAsyncThunk('warehouseReleases/goToNextPage', (_, { getState, dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { pageInfo } = state.warehouseReleases;
    if (!pageInfo.hasNextPage || !pageInfo.endCursor) {
        return rejectWithValue('No more pages');
    }
    return dispatch(fetchWarehouseReleases({ afterCursor: pageInfo.endCursor }));
}));
export const goToPreviousWarehouseReleasesPage = createAsyncThunk('warehouseReleases/goToPreviousPage', (_, { getState, dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { currentPage } = state.warehouseReleases;
    if (currentPage <= 1) {
        return rejectWithValue('Already on first page');
    }
    return dispatch(goToWarehouseReleasesPage(currentPage - 1));
}));
export const addRelease = createAsyncThunk('warehouseReleases/addRelease', () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const warehouseReleaseData = yield client.mutate({
            mutation: ADD_WAREHOUSE_RELEASE,
            fetchPolicy: 'no-cache',
        });
        return warehouseReleaseData.data.addWarehouseRelease.warehouseRelease;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeRelease = createAsyncThunk('warehouseReleases/removeRelease', (warehouseReleaseId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_WAREHOUSE_RELEASE,
            variables: { warehouseReleaseNodeId: warehouseReleaseId },
        });
        return warehouseReleaseId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const duplicateRelease = createAsyncThunk('warehouseReleases/duplicateRelease', (warehouseReleaseId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const warehouseReleaseData = yield client.mutate({
            mutation: DUPLICATE_WAREHOUSE_RELEASE,
            variables: { warehouseReleaseNodeId: warehouseReleaseId },
        });
        return warehouseReleaseData.data.duplicateWarehouseReleaseAsDraft.warehouseRelease.id;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const warehouseReleasesSlice = createSlice({
    name: 'warehouseReleases',
    initialState,
    reducers: {
        resetWarehouseReleasesState(state) {
            state.items = [];
            state.status.fetch = 'idle';
            state.errors.fetch = null;
            state.refetchFlags.fetch = false;
        },
        setWarehouseReleasesRefetchFlag(state, action) {
            state.refetchFlags.fetch = action.payload;
        },
        setFilterSearch(state, action) {
            const { value } = action.payload;
            state.filters.search = value;
        },
        setFilterOrder(state, action) {
            const { value } = action.payload;
            state.filters.order = value;
        },
        setFilterStatuses(state, action) {
            const { value } = action.payload;
            if (state.filters.statuses.includes(value)) {
                state.filters.statuses = state.filters.statuses.filter((status) => status !== value);
            }
            else {
                state.filters.statuses = [...state.filters.statuses, value];
            }
        },
        resetFilters(state) {
            state.filters = {
                search: '',
                order: '-modifiedDate',
                statuses: [],
            };
            state.cursors = [''];
            state.currentPage = 1;
        },
        resetPagination(state) {
            state.cursors = [''];
            state.currentPage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWarehouseReleases.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchWarehouseReleases.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.items = action.payload.warehouseReleases;
            state.pageInfo = action.payload.pageInfo;
            state.refetchFlags.fetch = false;
            if (action.payload.totalCount !== undefined && action.payload.totalCount !== null) {
                state.totalCount = action.payload.totalCount;
            }
            const { afterCursor, pageInfo } = action.payload;
            if (afterCursor === '') {
                state.currentPage = 1;
                if (state.cursors.length === 0 || state.cursors[0] !== '') {
                    state.cursors = [''];
                }
            }
            else if (afterCursor.startsWith('offset:')) {
                const offset = parseInt(afterCursor.substring(7), 10);
                state.currentPage = Math.floor(offset / WAREHOUSE_RELEASES_PAGE_SIZE) + 1;
            }
            else {
                const cursorIndex = state.cursors.indexOf(afterCursor);
                if (cursorIndex !== -1) {
                    state.currentPage = cursorIndex + 1;
                }
                else {
                    state.cursors.push(afterCursor);
                    state.currentPage = state.cursors.length;
                }
            }
            if (pageInfo.hasNextPage && pageInfo.endCursor) {
                const nextCursorIndex = state.currentPage;
                while (state.cursors.length <= nextCursorIndex) {
                    state.cursors.push('');
                }
                state.cursors[nextCursorIndex] = pageInfo.endCursor;
            }
        })
            .addCase(fetchWarehouseReleases.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.payload || 'Failed to fetch warehouse releases';
        })
            .addCase(addRelease.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addRelease.fulfilled, (state) => {
            state.status.add = 'succeeded';
        })
            .addCase(addRelease.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add warehouse release';
        })
            .addCase(removeRelease.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeRelease.fulfilled, (state, action) => {
            state.items = state.items.filter((warehouseRelease) => warehouseRelease.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeRelease.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        })
            .addCase(duplicateRelease.pending, (state) => {
            state.status.duplicate = 'loading';
        })
            .addCase(duplicateRelease.fulfilled, (state) => {
            state.status.duplicate = 'succeeded';
            state.refetchFlags.fetch = true;
        })
            .addCase(duplicateRelease.rejected, (state, action) => {
            state.status.duplicate = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { resetWarehouseReleasesState, setWarehouseReleasesRefetchFlag, setFilterSearch, setFilterOrder, setFilterStatuses, resetFilters, resetPagination, } = warehouseReleasesSlice.actions;
export const selectWarehouseReleasesStatus = (state) => state.warehouseReleases.status;
export const selectWarehouseReleasesErrors = (state) => state.warehouseReleases.errors;
export const selectWarehouseReleasesRefetchFlags = (state) => state.warehouseReleases.refetchFlags;
export const selectWarehouseReleasesFilters = (state) => state.warehouseReleases.filters;
export const selectWarehouseReleases = (state) => state.warehouseReleases.items;
export const selectWarehouseReleasesPageInfo = (state) => state.warehouseReleases.pageInfo;
export const selectWarehouseReleasesCurrentPage = (state) => state.warehouseReleases.currentPage;
export const selectWarehouseReleasesTotalCount = (state) => state.warehouseReleases.totalCount;
export const selectWarehouseReleasesPageNumbers = (state) => {
    const { cursors } = state.warehouseReleases;
    const pages = [];
    for (let i = 1; i <= cursors.length; i += 1) {
        pages.push(i);
    }
    return pages;
};
export default warehouseReleasesSlice.reducer;
